import { FilterExpr } from '@core/filters/path-filter/wire';
import { FilterableColCfg } from '@core/models/entities/entity-visualization-interface';
import { OrderBy } from '@core/models/entity/entity-selector-request.model';
import {
  FilterPresetModel,
  FilterPresetPayload,
  FilterPresetScope,
  StoredFilterPresetPayload
} from '@core/models/filter-preset.model';
import { FilterGrammar } from '@core/store/user-preferences/user-preferences.model';
import { isNil } from '@core/utils/shared-utils';

import {
  AvailableFilterField,
  FilterCondition,
  FilterConfiguration,
  FilterGroup
} from '@shared/components/filter-query-builder/filter.models';
import { SearchResult } from '@shared/components/search-query-builder/search-query-builder.component';
import { SortDirection } from '@shared/components/table/table.model';

import {
  convertSearchResultToFilterConfiguration,
  mergeFilterConfigurations
} from './search-filter.utils';

/**
 * A column the entity table can sort on, in both the identity a preset stores
 * (`dbColName` / `labelKey`) and the one the table uses (`columnKey`). Built by the entity
 * component from its sortable-column config, so the preset store never has to reach into
 * the entity schema itself.
 */
export interface SortableFieldRef {
  /** `TableColumn.key`, which is what `SortState.column` holds. */
  columnKey: string;
  dbColName: string;
  labelKey?: string;
  /** Translated header, for the save dialog's recap. */
  label: string;
}

/** A stored sort resolved back to something the table can be seeded with. */
export interface RestoredSort {
  column: string;
  direction: SortDirection;
}

/**
 * Fields the filter builder emits as literals rather than as a column name: the target
 * lives in `labelKey` / `tagNames` / `values` instead. They carry no dependency on the
 * entity schema, so they stay valid in a dataspace the preset was not created in.
 */
const ENTITY_AGNOSTIC_FIELDS = new Set(['labels', 'tags', 'uid']);

let restoredIdCounter = 0;

function nextId(prefix: string): string {
  return `${prefix}-${Date.now()}-${restoredIdCounter++}`;
}

function isGroup(item: FilterCondition | FilterGroup): item is FilterGroup {
  return 'conditions' in item && !('operation' in item);
}

function hasConditions(config?: FilterConfiguration): boolean {
  return (config?.rootGroup.conditions.length ?? 0) > 0;
}

/**
 * Reduce what drives the entity table — the search bar, the advanced filter builder and
 * the column sort — to the `(filters, search, sort)` triple a preset stores.
 *
 * This runs the same reduction as the data request itself
 * (`PagedEntityService.fetchEntities`), so a preset always reproduces the rows the user
 * was looking at when they saved it. Search-bar qualifiers become filter conditions
 * keyed by DB column name, which is what makes a preset independent of the language it
 * was created in; only the free-text terms stay as text.
 *
 * @param translatedToCfg Filterable columns keyed by translated label, as built by
 *   `makeTranslatedFilterCfgMap` — the keys must match the qualifier keys the search bar
 *   produced.
 * @param sort The resolved `orderBy` of the live table, as `EntityTableService.getOrderBy`
 *   builds it for the data request.
 */
export function buildFilterPresetPayload(
  searchResult: SearchResult | null,
  advancedFilters: FilterConfiguration | null,
  translatedToCfg: Record<string, FilterableColCfg>,
  sort?: OrderBy[]
): FilterPresetPayload {
  const searchBarFilters = isNil(searchResult)
    ? undefined
    : convertSearchResultToFilterConfiguration(searchResult, translatedToCfg);

  const merged = mergeFilterConfigurations(searchBarFilters, advancedFilters ?? undefined);
  const search = searchResult?.textTerms.join(' ').trim();

  return {
    ...(hasConditions(merged) ? { filters: merged } : {}),
    ...(search ? { search } : {}),
    ...(sort?.length ? { sort } : {})
  };
}

/**
 * What saving a Path filter stores: the composer's tree and the sort. Never the search bar's text,
 * which stays out of presets, and never `filters`: the two grammars are not mixed in one preset.
 */
export function buildPathFilterPresetPayload(
  pathFilter: FilterExpr | undefined,
  sort: OrderBy[]
): FilterPresetPayload {
  return {
    ...(pathFilter ? { pathFilter } : {}),
    ...(sort.length > 0 ? { sort } : {})
  };
}

/**
 * A Path preset saved with no tree still stores its related-entity sorts, which only the Path
 * grammar can express.
 */
export function isPathPreset(
  payload: Pick<Partial<StoredFilterPresetPayload>, 'pathFilter' | 'sort'>
): boolean {
  return (
    !isNil(payload.pathFilter) ||
    (payload.sort ?? []).some((entry) => !isNil(entry.association))
  );
}

/**
 * The grammar a stored preset is written in. `undefined` for one holding only free text and root
 * sorts: nothing stored says which grammar saved it, and either can apply it.
 */
export function storedPresetGrammar(
  payload: Pick<Partial<StoredFilterPresetPayload>, 'filters' | 'pathFilter' | 'sort'>
): FilterGrammar | undefined {
  if (isPathPreset(payload)) return 'path';
  return isNil(payload.filters) ? undefined : 'classic';
}

/** Number of leaf conditions in a stored filter tree, nested groups included. */
export function countFilterConditions(filters?: FilterConfiguration | null): number {
  if (isNil(filters)) {
    return 0;
  }
  const count = (group: FilterGroup): number =>
    group.conditions.reduce((total, item) => total + (isGroup(item) ? count(item) : 1), 0);
  return count(filters.rootGroup);
}

/**
 * Picks the preset to apply when an entity table opens. Both a USER and a DATASPACE
 * preset can carry the default flag for the same entity — they live in separate buckets
 * server-side — so the tie is broken here: the user's own choice overrides the one their
 * dataspace ships.
 *
 * Only presets of the given context are eligible. A reference picker lists the entity-wide
 * presets alongside its own, but opening it must not silently narrow the list to whatever
 * default an entity table happens to carry, so the two never fall back to each other.
 */
export function resolveDefaultPreset(
  presets: FilterPresetModel[],
  context?: string
): FilterPresetModel | undefined {
  const eligible = presets.filter(
    (preset) => (preset.context ?? undefined) === context && preset.isDefault
  );
  return (
    eligible.find((preset) => preset.scope === FilterPresetScope.USER) ??
    eligible.find((preset) => preset.scope === FilterPresetScope.DATASPACE)
  );
}

/**
 * Prepare a stored payload for injection back into the filter builder and the table.
 *
 * Conditions are matched against `availableFields` the same way the builder resolves
 * them, so every kept condition is renderable as a pill. A preset is reusable across
 * dataspaces whose entity schemas differ, so a condition on a field that no longer
 * exists is dropped and reported rather than sent to the backend. Ids are re-issued
 * because the builder keys pill editing by id, and applying one preset twice would
 * otherwise produce collisions.
 *
 * `sort` gets the same treatment through `sortableFields`, and stays `undefined` when the
 * preset carries none — that absence is what leaves the table's current order alone.
 *
 * A Path tree is returned as stored: its conditions are not tied to the entity's columns.
 */
export function restoreFilterPresetPayload(
  payload: Partial<StoredFilterPresetPayload>,
  availableFields: AvailableFilterField[],
  sortableFields: SortableFieldRef[] = []
): {
  filters?: FilterConfiguration;
  search?: string;
  sort?: RestoredSort[];
  pathFilter?: FilterExpr;
  /** Every stored sort but label ones, related-entity sorts included. */
  pathSort?: OrderBy[];
  droppedFields: string[];
} {
  const droppedFields: string[] = [];
  const search = payload.search?.trim();

  if (isPathPreset(payload)) {
    // A label sort has no Path spelling: it goes back to a table column, for a host whose header
    // sort still applies beside the Path list.
    const pathSort = payload.sort?.filter((entry) => isNil(entry.labelKey)) ?? [];
    const labelSort = payload.sort?.filter((entry) => !isNil(entry.labelKey)) ?? [];
    return {
      search: search || undefined,
      pathFilter: payload.pathFilter ?? undefined,
      ...(pathSort.length > 0 ? { pathSort } : {}),
      sort: isNil(payload.sort)
        ? undefined
        : restoreSort(labelSort, sortableFields, droppedFields),
      droppedFields: [...new Set(droppedFields)]
    };
  }

  const resolvableFields = new Set(
    availableFields.map((field) => field.dbColumnName || field.field)
  );
  const rootGroup = isNil(payload.filters)
    ? undefined
    : pruneGroup(payload.filters.rootGroup, resolvableFields, droppedFields);

  return {
    filters: isNil(rootGroup) ? undefined : { rootGroup },
    search: search || undefined,
    sort: restoreSort(payload.sort, sortableFields, droppedFields),
    droppedFields: [...new Set(droppedFields)]
  };
}

/**
 * A stored entry identifies its column by `(dbColName, labelKey)`, not by `dbColName`
 * alone: a label column carries `dbColName === labelKey === <the label's name>`, so a
 * label sort and a real column of that name are indistinguishable on `dbColName`.
 */
function sortKeyOf(dbColName: string, labelKey?: string): string {
  return `${dbColName}::${labelKey ?? ''}`;
}

/**
 * Returns `undefined` when the preset stores no sort, so callers can tell "no sort saved"
 * from "a sort was saved and none of it resolved" — the first leaves the table untouched,
 * the second clears it.
 */
function restoreSort(
  sort: OrderBy[] | null | undefined,
  sortableFields: SortableFieldRef[],
  droppedFields: string[]
): RestoredSort[] | undefined {
  if (isNil(sort)) {
    return undefined;
  }

  const columnKeys = new Map(
    sortableFields.map((field) => [
      sortKeyOf(field.dbColName, field.labelKey),
      field.columnKey
    ])
  );

  const restored: RestoredSort[] = [];
  for (const entry of sort) {
    // Columns are the root entity's; the same name on a related entity is another value.
    if (!isNil(entry.association)) {
      droppedFields.push(`${entry.association.edge}.${entry.labelKey ?? entry.field}`);
      continue;
    }
    const columnKey = columnKeys.get(sortKeyOf(entry.field, entry.labelKey));
    if (isNil(columnKey)) {
      droppedFields.push(entry.labelKey ?? entry.field);
      continue;
    }
    restored.push({ column: columnKey, direction: entry.order });
  }

  return restored;
}

/**
 * Returns `undefined` for a group left without conditions, so an empty group never
 * reaches `convertFilterConfigurationToBackEndFormat`.
 */
function pruneGroup(
  group: FilterGroup,
  resolvableFields: Set<string>,
  droppedFields: string[]
): FilterGroup | undefined {
  const conditions: (FilterCondition | FilterGroup)[] = [];

  for (const item of group.conditions) {
    if (isGroup(item)) {
      const pruned = pruneGroup(item, resolvableFields, droppedFields);
      if (!isNil(pruned)) {
        conditions.push(pruned);
      }
      continue;
    }

    if (ENTITY_AGNOSTIC_FIELDS.has(item.field) || resolvableFields.has(item.field)) {
      conditions.push({ ...item, id: nextId('preset-filter') });
    } else {
      droppedFields.push(item.field);
    }
  }

  if (conditions.length === 0) {
    return undefined;
  }

  return { ...group, id: nextId('preset-group'), conditions };
}
